export default {
  created() {
    this.geocoderInputEl = null;
    this.geocoderInputListener = null;
    this.geocoderInputDebounceTimer = null;
    this.geocoderRenderDebounceTimer = null;
    this.geocoderOfferSuggestRequestSeq = 0;
    this.geocoderTourismRegionSuggestRequestSeq = 0;
    this.externalSuggestionsByQuery = {};
    this.tourismRegionSuggestionsByQuery = {};
    this.mapboxSuggestionsByQuery = {};
    this.forceNextSearchSortRelevance = false;
  },

  beforeDestroy() {
    if (this.geocoderInputEl && this.geocoderInputListener) {
      this.geocoderInputEl.removeEventListener('input', this.geocoderInputListener);
    }
    if (this.geocoderInputDebounceTimer) {
      clearTimeout(this.geocoderInputDebounceTimer);
      this.geocoderInputDebounceTimer = null;
    }
    if (this.geocoderRenderDebounceTimer) {
      clearTimeout(this.geocoderRenderDebounceTimer);
      this.geocoderRenderDebounceTimer = null;
    }
  },

  methods: {
    logMapboxVoivodshipDebug(message, payload = null) {
      // Temporary debug helper for voivodship filtering diagnostics.
      if (payload === null) {
        console.log(`[GeocoderVoivodshipDebug] ${message}`);
        return;
      }
      console.log(`[GeocoderVoivodshipDebug] ${message}`, payload);
    },

    normalizeRegionLabel(rawValue) {
      const lowered = `${rawValue || ''}`
        .trim()
        .toLowerCase();
      const asciiValue = (typeof lowered.normalize === 'function')
        ? lowered.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        : lowered;

      return asciiValue
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    },

    normalizeVoivodshipSourceToList(source) {
      if (Array.isArray(source)) {
        return source;
      }

      if (typeof source !== 'string') {
        return [];
      }

      const trimmedSource = source.trim();
      if (trimmedSource === '') {
        return [];
      }

      if (trimmedSource.startsWith('[')) {
        try {
          const parsedSource = JSON.parse(trimmedSource);
          if (Array.isArray(parsedSource)) {
            return parsedSource;
          }
        } catch (e) {
          // Fallback to CSV parsing below.
        }
      }

      return trimmedSource.split(',');
    },

    resolveAllowedMapboxVoivodships() {
      const propSource = typeof this.allowedMapboxVoivodships !== 'undefined'
        ? this.allowedMapboxVoivodships
        : [];
      const globalSource = (typeof GLOBAL !== 'undefined' && GLOBAL && GLOBAL.mapboxGeocoderAllowedVoivodships)
        ? GLOBAL.mapboxGeocoderAllowedVoivodships
        : [];
      let list = this.normalizeVoivodshipSourceToList(propSource);
      if (list.length === 0) {
        list = this.normalizeVoivodshipSourceToList(globalSource);
      }
      const normalizedList = list
        .map((item) => this.normalizeRegionLabel(item))
        .filter((item) => item !== '');

      const resolved = Array.from(new Set(normalizedList));
      this.logMapboxVoivodshipDebug('Resolved allowed voivodships', {
        source,
        list,
        resolved,
      });
      return resolved;
    },

    resolveRegionNameByMapboxShortCode(rawCode) {
      const normalizedCode = `${rawCode || ''}`.trim().toUpperCase();
      const map = {
        'PL-02': 'dolnoslaskie',
        'PL-04': 'kujawsko-pomorskie',
        'PL-06': 'lubelskie',
        'PL-08': 'lubuskie',
        'PL-10': 'lodzkie',
        'PL-12': 'malopolskie',
        'PL-14': 'mazowieckie',
        'PL-16': 'opolskie',
        'PL-18': 'podkarpackie',
        'PL-20': 'podlaskie',
        'PL-22': 'pomorskie',
        'PL-24': 'slaskie',
        'PL-26': 'swietokrzyskie',
        'PL-28': 'warminsko-mazurskie',
        'PL-30': 'wielkopolskie',
        'PL-32': 'zachodniopomorskie',
      };

      return map[normalizedCode] || '';
    },

    resolveMapboxFeatureRegionCandidates(feature) {
      const featureContext = Array.isArray(feature && feature.context) ? feature.context : [];
      const candidates = [];

      featureContext.forEach((contextItem) => {
        const contextId = `${contextItem && contextItem.id ? contextItem.id : ''}`.toLowerCase();
        if (contextId.startsWith('region.') || contextId.startsWith('region_')) {
          candidates.push(this.normalizeRegionLabel(contextItem && contextItem.text_pl ? contextItem.text_pl : ''));
          candidates.push(this.normalizeRegionLabel(contextItem && contextItem.text ? contextItem.text : ''));
          candidates.push(this.normalizeRegionLabel(this.resolveRegionNameByMapboxShortCode(
            contextItem && contextItem.short_code ? contextItem.short_code : ''
          )));
        }
      });

      const placeNamePl = `${feature && feature.place_name_pl ? feature.place_name_pl : ''}`.trim();
      if (placeNamePl !== '') {
        candidates.push(this.normalizeRegionLabel(placeNamePl));
      }
      const placeName = `${feature && feature.place_name ? feature.place_name : ''}`.trim();
      if (placeName !== '') {
        candidates.push(this.normalizeRegionLabel(placeName));
      }

      const resolvedCandidates = Array.from(new Set(candidates.filter((item) => item !== '')));
      this.logMapboxVoivodshipDebug('Feature region candidates', {
        featureId: feature && feature.id ? feature.id : null,
        featureText: feature && feature.text ? feature.text : null,
        rawContext: feature && Array.isArray(feature.context) ? feature.context : [],
        resolvedCandidates,
      });
      return resolvedCandidates;
    },

    isMapboxSuggestionAllowed(feature) {
      const allowedVoivodships = this.resolveAllowedMapboxVoivodships();
      if (allowedVoivodships.length === 0) {
        this.logMapboxVoivodshipDebug('Rejected feature because allowed list is empty', {
          featureId: feature && feature.id ? feature.id : null,
          featureText: feature && feature.text ? feature.text : null,
        });
        return false;
      }

      const featureRegionCandidates = this.resolveMapboxFeatureRegionCandidates(feature);
      if (featureRegionCandidates.length === 0) {
        this.logMapboxVoivodshipDebug('Rejected feature because no region candidates found', {
          featureId: feature && feature.id ? feature.id : null,
          featureText: feature && feature.text ? feature.text : null,
          allowedVoivodships,
        });
        return false;
      }

      const isAllowed = featureRegionCandidates.some((candidate) => (
        allowedVoivodships.some((allowed) => candidate.includes(allowed))
      ));
      this.logMapboxVoivodshipDebug('Feature allow/deny decision', {
        featureId: feature && feature.id ? feature.id : null,
        featureText: feature && feature.text ? feature.text : null,
        allowedVoivodships,
        featureRegionCandidates,
        isAllowed,
      });
      return isAllowed;
    },

    isFeatureFlagEnabled(featureKey) {
      let featureValue;
      if (this.$store && this.$store.getters && typeof this.$store.getters.isFeatureEnabled === 'function') {
        featureValue = this.$store.getters.isFeatureEnabled(featureKey);
      } else if (
        typeof GLOBAL !== 'undefined'
        && GLOBAL
        && GLOBAL.features
        && typeof GLOBAL.features[featureKey] !== 'undefined'
      ) {
        featureValue = GLOBAL.features[featureKey];
      } else {
        return false;
      }

      if (featureValue === true) {
        return true;
      }
      if (featureValue === false || featureValue === null || typeof featureValue === 'undefined') {
        return false;
      }

      const normalizedValue = `${featureValue}`.trim().toLowerCase();
      return normalizedValue === '1' || normalizedValue === 'true' || normalizedValue === 'on' || normalizedValue === 'yes';
    },

    isOfferSuggestionsEnabled() {
      return this.isFeatureFlagEnabled('search_offer_suggestions');
    },

    isOfferSuggestionsDirectEnabled() {
      return this.isFeatureFlagEnabled('search_offer_suggestions_direct');
    },

    isTourismRegionSuggestionsEnabled() {
      return true;
    },

    hasAutosuggestLocationPoint() {
      const point = this.resolveAutosuggestLocationPoint();
      const lat = Number.parseFloat(point && point.lat);
      const lng = Number.parseFloat(point && point.lng);

      return Number.isFinite(lat) && Number.isFinite(lng);
    },

    shouldRequestExternalSuggestions() {
      return this.isOfferSuggestionsEnabled() && this.hasAutosuggestLocationPoint();
    },

    resolveGeocoderSuggestionRenderLimit() {
      return 10;
    },

    resolveExternalSuggestionRequestLimit() {
      return 5;
    },

    resolveExternalSuggestionLabel() {
      return 'Oferty';
    },

    resolveTourismRegionSuggestionLabel() {
      return 'Regiony turystyczne';
    },

    resolveExternalAggregateSuggestionLabel(totalMatches) {
      return `Najtrafniejsze oferty w Twojej okolicy: ${totalMatches}`;
    },

    resolveRefineSuggestionLabel(remainingMatches) {
      return `... doprecyzuj, by zobaczyć ${remainingMatches} dalszych rezultatów`;
    },

    resolveMapboxSuggestionLabel() {
      return 'Miejscowości';
    },

    // Optional per-component hook for params like spatial_d/product_type.
    resolveExternalSuggestionExtraParams() {},

    normalizeSearchPhrase(rawValue) {
      return `${rawValue || ''}`.replace(/\s+/g, ' ').trim();
    },

    normalizeAutosuggestQueryKey(rawValue) {
      return this.normalizeSearchPhrase(rawValue).toLowerCase();
    },

    bindGeocoderInputTracking() {
      this.geocoderInputEl = this.$el.querySelector('.mapboxgl-ctrl-geocoder--input');
      if (!this.geocoderInputEl) {
        return;
      }

      if (this.geocoderInputListener) {
        this.geocoderInputEl.removeEventListener('input', this.geocoderInputListener);
      }

      this.geocoderInputListener = () => {
        this.manualSearchPhrase = this.normalizeSearchPhrase(this.geocoderInputEl.value);
        this.lastInteractionWasGeocoderSelection = false;
        this.scheduleExternalSuggestionsRefresh(this.manualSearchPhrase);
      };
      this.geocoderInputEl.addEventListener('input', this.geocoderInputListener);

      this.geocoderInputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          if (typeof this.submitForm === 'function') {
            this.submitForm();
          }
        }
      });
    },

    scheduleExternalSuggestionsRefresh(searchInput) {
      if (this.geocoderInputDebounceTimer) {
        clearTimeout(this.geocoderInputDebounceTimer);
      }

      const normalizedPhrase = this.normalizeSearchPhrase(searchInput);
      const queryKey = this.normalizeAutosuggestQueryKey(normalizedPhrase);
      if (queryKey.length < 2) {
        return;
      }
      const shouldRequestOffers = this.shouldRequestExternalSuggestions();
      const shouldRequestTourismRegions = this.isTourismRegionSuggestionsEnabled();
      if (!shouldRequestOffers) {
        this.externalSuggestionsByQuery[queryKey] = [];
      }
      if (!shouldRequestTourismRegions) {
        this.tourismRegionSuggestionsByQuery[queryKey] = [];
      }
      if (!shouldRequestOffers && !shouldRequestTourismRegions) {
        this.scheduleMergedSuggestionsRender(queryKey);
        return;
      }

      this.geocoderInputDebounceTimer = setTimeout(async () => {
        const latestInputKey = this.normalizeAutosuggestQueryKey(this.resolveGeocoderInputValue());
        if (latestInputKey !== queryKey) {
          return;
        }

        await Promise.all([
          shouldRequestOffers ? this.fetchExternalGeocoderSuggestions(normalizedPhrase) : Promise.resolve([]),
          shouldRequestTourismRegions ? this.fetchTourismRegionGeocoderSuggestions(normalizedPhrase) : Promise.resolve([]),
        ]);
        this.scheduleMergedSuggestionsRender(queryKey);
      }, 220);
    },

    resolveGeocoderInputValue() {
      if (this.geocoderInputEl) {
        return this.normalizeSearchPhrase(this.geocoderInputEl.value);
      }

      const fallbackInputEl = this.$el.querySelector('.mapboxgl-ctrl-geocoder--input');
      if (!fallbackInputEl) {
        return '';
      }

      return this.normalizeSearchPhrase(fallbackInputEl.value);
    },

    escapeHtml(inputValue) {
      return `${inputValue || ''}`
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    resolveLocalizedDetailViewUrl(detailViewUrlList) {
      if (!Array.isArray(detailViewUrlList)) {
        return '';
      }

      const currentLocale = `${document.documentElement.lang || ''}`.trim().toLowerCase();
      let fallbackUrl = '';

      for (const urlData of detailViewUrlList) {
        const rawValue = `${urlData || ''}`.trim();
        if (rawValue === '') {
          continue;
        }

        const parts = rawValue.split('||');
        if (parts.length < 2) {
          if (fallbackUrl === '') {
            fallbackUrl = rawValue;
          }
          continue;
        }

        const locale = `${parts[0] || ''}`.trim().toLowerCase();
        const url = `${parts[1] || ''}`.trim();
        if (url === '') {
          continue;
        }

        if (fallbackUrl === '') {
          fallbackUrl = url;
        }

        if (locale !== '' && locale === currentLocale) {
          return url;
        }
      }

      return fallbackUrl;
    },

    resolveFulltextSuggestionTargetUrl(result) {
      const resultProperties = result && result.properties ? result.properties : {};
      const detailViewUrl = resultProperties.fulltextDetailViewUrl;

      if (Array.isArray(detailViewUrl)) {
        return this.resolveLocalizedDetailViewUrl(detailViewUrl);
      }

      return `${detailViewUrl || ''}`.trim();
    },

    openFulltextSuggestionInNewTab(result) {
      const targetUrl = this.resolveFulltextSuggestionTargetUrl(result);
      if (targetUrl === '') {
        return false;
      }

      window.open(targetUrl, '_blank', 'noopener,noreferrer');
      return true;
    },

    isRefineSuggestion(result) {
      const resultProperties = result && result.properties ? result.properties : {};

      return resultProperties.isRefineSuggestion === true;
    },

    buildRefineSuggestion(id, remainingMatches, coordinates, label = '') {
      const title = this.resolveRefineSuggestionLabel(remainingMatches);

      return {
        id,
        type: 'Feature',
        place_type: ['refine'],
        place_name: title,
        text: title,
        center: coordinates,
        geometry: {
          type: 'Point',
          coordinates,
        },
        properties: {
          isRefineSuggestion: true,
          refineSuggestionLabel: label,
        },
      };
    },

    isFulltextAggregateSuggestion(result) {
      const resultProperties = result && result.properties ? result.properties : {};

      return resultProperties.isFulltextAggregateSuggestion === true;
    },

    submitAggregateSuggestionAsSearch(result) {
      if (!this.isFulltextAggregateSuggestion(result)) {
        return false;
      }

      this.preserveTypedPhraseAfterFulltextSelection(result);
      this.lastInteractionWasGeocoderSelection = false;
      this.forceNextSearchSortRelevance = true;
      if (typeof this.submitForm === 'function') {
        this.submitForm();
      }

      return true;
    },

    consumeForcedRelevanceSortFlag() {
      const shouldForceRelevance = this.forceNextSearchSortRelevance === true;
      this.forceNextSearchSortRelevance = false;

      return shouldForceRelevance;
    },

    preserveTypedPhraseAfterFulltextSelection(result) {
      const resultProperties = result && result.properties ? result.properties : {};
      const phrase = this.normalizeSearchPhrase(
        resultProperties.fulltextSearchPhrase || this.manualSearchPhrase || ''
      );
      if (phrase === '') {
        return;
      }

      this.manualSearchPhrase = phrase;
      if (this.geocoderInputEl) {
        this.geocoderInputEl.value = phrase;
      }

      // Mapbox may update input asynchronously after result selection; enforce typed phrase again.
      setTimeout(() => {
        if (this.geocoderInputEl) {
          this.geocoderInputEl.value = phrase;
        }
      }, 0);
    },

    renderGeocoderSuggestion(feature) {
      const featureProps = feature && feature.properties ? feature.properties : {};
      const isRefineSuggestion = featureProps.isRefineSuggestion === true;
      if (isRefineSuggestion) {
        const title = this.escapeHtml(feature.text || feature.place_name || '');
        const label = this.escapeHtml(featureProps.refineSuggestionLabel || '');
        const separatorHtml = label !== ''
          ? `<div class="mapboxgl-ctrl-geocoder--fulltext-separator">${label}</div>`
          : '';

        return `<div class="mapboxgl-ctrl-geocoder--suggestion">${separatorHtml}<div class="mapboxgl-ctrl-geocoder--suggestion-title text-muted">${title}</div></div>`;
      }

      const isFulltextSuggestion = featureProps.isFulltextSuggestion === true;
      if (isFulltextSuggestion) {
        const title = this.escapeHtml(feature.text || feature.place_name || '');
        const label = this.escapeHtml(featureProps.fulltextSuggestionLabel || '');
        const separatorHtml = label !== ''
          ? `<div class="mapboxgl-ctrl-geocoder--fulltext-separator">${label}</div>`
          : '';

        return `<div class="mapboxgl-ctrl-geocoder--suggestion">${separatorHtml}<div class="mapboxgl-ctrl-geocoder--suggestion-title">${title}</div></div>`;
      }

      const isTourismRegionSuggestion = featureProps.isTourismRegionSuggestion === true;
      if (isTourismRegionSuggestion) {
        const title = this.escapeHtml(feature.text || feature.place_name || '');
        const label = this.escapeHtml(featureProps.tourismRegionSuggestionLabel || '');
        const separatorHtml = label !== ''
          ? `<div class="mapboxgl-ctrl-geocoder--fulltext-separator">${label}</div>`
          : '';

        return `<div class="mapboxgl-ctrl-geocoder--suggestion">${separatorHtml}<div class="mapboxgl-ctrl-geocoder--suggestion-title">${title}</div></div>`;
      }

      const placeName = `${feature && feature.place_name ? feature.place_name : ''}`;
      const placeParts = placeName.split(',');
      const title = this.escapeHtml(placeParts[0] || '');
      const address = this.escapeHtml(placeParts.slice(1).join(',').trim());
      const mapboxLabel = this.escapeHtml(featureProps.mapboxSuggestionLabel || '');
      const separatorHtml = mapboxLabel !== ''
        ? `<div class="mapboxgl-ctrl-geocoder--fulltext-separator">${mapboxLabel}</div>`
        : '';

      return `<div class="mapboxgl-ctrl-geocoder--suggestion">${separatorHtml}<div class="mapboxgl-ctrl-geocoder--suggestion-title">${title}</div><div class="mapboxgl-ctrl-geocoder--suggestion-address">${address}</div></div>`;
    },

    async fetchExternalGeocoderSuggestions(searchInput) {
      const normalizedPhrase = this.normalizeSearchPhrase(searchInput);
      const queryKey = this.normalizeAutosuggestQueryKey(normalizedPhrase);
      if (queryKey.length < 2) {
        this.externalSuggestionsByQuery[queryKey] = [];
        return [];
      }
      if (!this.isOfferSuggestionsEnabled()) {
        this.externalSuggestionsByQuery[queryKey] = [];
        return [];
      }
      if (!this.shouldRequestExternalSuggestions()) {
        this.externalSuggestionsByQuery[queryKey] = [];
        return [];
      }

      const requestSeq = ++this.geocoderOfferSuggestRequestSeq;
      const suggestPoint = this.resolveAutosuggestLocationPoint();
      const params = new URLSearchParams();
      params.set('q', normalizedPhrase);
      params.set('spatial_pt', `${suggestPoint.lat},${suggestPoint.lng}`);
      params.set('limit', `${this.resolveExternalSuggestionRequestLimit()}`);
      this.resolveExternalSuggestionExtraParams(params);

      let suggestUrl = '';
      try {
        suggestUrl = JsRouting.generate('webapi_search_suggest', {}, true);
      } catch (routingError) {
        return [];
      }

      const requestUrl = `${suggestUrl}${suggestUrl.indexOf('?') >= 0 ? '&' : '?'}${params.toString()}`;
      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (!response.ok) {
          return [];
        }
        const responseData = await response.json();
        if (requestSeq !== this.geocoderOfferSuggestRequestSeq) {
          return [];
        }

        const suggestions = Array.isArray(responseData && responseData.suggestions)
          ? responseData.suggestions
          : [];
        const rawTotal = responseData && typeof responseData.total !== 'undefined'
          ? Number.parseInt(responseData.total, 10)
          : suggestions.length;
        const totalMatches = Number.isFinite(rawTotal) && rawTotal > 0
          ? rawTotal
          : suggestions.length;
        const label = this.resolveExternalSuggestionLabel();
        const autosuggestPoint = this.resolveAutosuggestLocationPoint();
        const fallbackCoordinates = [autosuggestPoint.lng, autosuggestPoint.lat];
        let mappedSuggestions = [];

        if (!this.isOfferSuggestionsDirectEnabled()) {
          if (totalMatches > 0) {
            const aggregateLabel = this.resolveExternalAggregateSuggestionLabel(totalMatches);
            mappedSuggestions = [{
              id: 'fulltext.aggregate',
              type: 'Feature',
              place_type: ['fulltext'],
              place_name: aggregateLabel,
              text: aggregateLabel,
              center: fallbackCoordinates,
              geometry: {
                type: 'Point',
                coordinates: fallbackCoordinates,
              },
              properties: {
                isFulltextSuggestion: true,
                isFulltextAggregateSuggestion: true,
                fulltextSuggestionLabel: label,
                fulltextSearchPhrase: normalizedPhrase,
                fulltextDetailViewUrl: [],
              },
            }];
          }
        } else {
          mappedSuggestions = suggestions.map((suggestion, index) => {
            const suggestionName = `${suggestion.name || ''}`.trim();
            if (suggestionName === '') {
              return null;
            }

            const detailUrl = Array.isArray(suggestion.detailUrl)
              ? suggestion.detailUrl
              : (Array.isArray(suggestion.detail_view_url) ? suggestion.detail_view_url : []);

            return {
              id: `fulltext.${index}`,
              type: 'Feature',
              place_type: ['fulltext'],
              place_name: suggestionName,
              text: suggestionName,
              center: fallbackCoordinates,
              geometry: {
                type: 'Point',
                coordinates: fallbackCoordinates,
              },
              properties: {
                isFulltextSuggestion: true,
                fulltextSuggestionLabel: index === 0 ? label : '',
                fulltextSearchPhrase: normalizedPhrase,
                fulltextDetailViewUrl: detailUrl,
              },
            };
          }).filter((item) => item !== null);

          const remainingMatches = totalMatches - suggestions.length;
          if (remainingMatches > 0) {
            mappedSuggestions.push(this.buildRefineSuggestion(
              'fulltext.refine',
              remainingMatches,
              fallbackCoordinates
            ));
          }
        }
        this.externalSuggestionsByQuery[queryKey] = mappedSuggestions;
        this.scheduleMergedSuggestionsRender(queryKey);

        return mappedSuggestions;
      } catch (error) {
        this.externalSuggestionsByQuery[queryKey] = [];
        return [];
      }
    },

    async fetchTourismRegionGeocoderSuggestions(searchInput) {
      const normalizedPhrase = this.normalizeSearchPhrase(searchInput);
      const queryKey = this.normalizeAutosuggestQueryKey(normalizedPhrase);
      if (queryKey.length < 2) {
        this.tourismRegionSuggestionsByQuery[queryKey] = [];
        return [];
      }

      const requestSeq = ++this.geocoderTourismRegionSuggestRequestSeq;
      const params = new URLSearchParams();
      params.set('q', normalizedPhrase);
      params.set('limit', `${this.resolveExternalSuggestionRequestLimit()}`);

      let suggestUrl = '';
      try {
        suggestUrl = JsRouting.generate('webapi_search_tourism_region_suggest', {}, true);
      } catch (routingError) {
        this.tourismRegionSuggestionsByQuery[queryKey] = [];
        return [];
      }

      const requestUrl = `${suggestUrl}${suggestUrl.indexOf('?') >= 0 ? '&' : '?'}${params.toString()}`;
      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        if (!response.ok) {
          this.tourismRegionSuggestionsByQuery[queryKey] = [];
          return [];
        }

        const responseData = await response.json();
        if (requestSeq !== this.geocoderTourismRegionSuggestRequestSeq) {
          return [];
        }

        const suggestions = Array.isArray(responseData && responseData.suggestions)
          ? responseData.suggestions
          : [];
        const rawTotal = responseData && typeof responseData.total !== 'undefined'
          ? Number.parseInt(responseData.total, 10)
          : suggestions.length;
        const totalMatches = Number.isFinite(rawTotal) && rawTotal > 0
          ? rawTotal
          : suggestions.length;
        const label = this.resolveTourismRegionSuggestionLabel();
        const mappedSuggestions = suggestions.map((suggestion, index) => {
          const suggestionName = `${suggestion.name || ''}`.trim();
          const lat = Number.parseFloat(suggestion.lat);
          const lng = Number.parseFloat(suggestion.lng);
          if (suggestionName === '' || !Number.isFinite(lat) || !Number.isFinite(lng)) {
            return null;
          }
          const radiusKm = Number.parseInt(suggestion.radius_km, 10);

          return {
            id: `tourism_region.${suggestion.type || 'region'}.${suggestion.id || index}`,
            type: 'Feature',
            place_type: ['tourism_region'],
            place_name: suggestionName,
            text: suggestionName,
            center: [lng, lat],
            geometry: {
              type: 'Point',
              coordinates: [lng, lat],
            },
            properties: {
              isTourismRegionSuggestion: true,
              tourismRegionSuggestionLabel: index === 0 ? label : '',
              tourismRegionType: suggestion.type || 'region',
              tourismRegionCode: suggestion.code || '',
              tourismRegionRadiusKm: Number.isFinite(radiusKm) && radiusKm > 0 ? radiusKm : null,
              tourismRegionCentroidRadiusKm: suggestion.centroid_radius_km || null,
            },
          };
        }).filter((item) => item !== null);

        const remainingMatches = totalMatches - suggestions.length;
        if (remainingMatches > 0) {
          const firstSuggestion = mappedSuggestions.length > 0 ? mappedSuggestions[0] : null;
          const coordinates = firstSuggestion && firstSuggestion.geometry && Array.isArray(firstSuggestion.geometry.coordinates)
            ? firstSuggestion.geometry.coordinates
            : [0, 0];
          mappedSuggestions.push(this.buildRefineSuggestion(
            'tourism_region.refine',
            remainingMatches,
            coordinates
          ));
        }

        this.tourismRegionSuggestionsByQuery[queryKey] = mappedSuggestions;
        this.scheduleMergedSuggestionsRender(queryKey);

        return mappedSuggestions;
      } catch (error) {
        this.tourismRegionSuggestionsByQuery[queryKey] = [];
        return [];
      }
    },

    mergeExternalSuggestionsIntoResultList(resultsPayload) {
      const queryKey = this.normalizeAutosuggestQueryKey(this.resolveGeocoderInputValue());
      if (queryKey.length < 2) {
        return;
      }

      const currentFeatures = Array.isArray(resultsPayload && resultsPayload.features)
        ? resultsPayload.features
        : [];
      const filteredMapboxFeatures = currentFeatures.filter((item) => !(
        item
        && item.properties
        && (item.properties.isFulltextSuggestion === true || item.properties.isTourismRegionSuggestion === true)
      )).filter((item) => this.isMapboxSuggestionAllowed(item));
      this.logMapboxVoivodshipDebug('Mapbox results filtered', {
        queryKey,
        rawCount: currentFeatures.length,
        filteredCount: filteredMapboxFeatures.length,
        filteredIds: filteredMapboxFeatures.map((item) => item && item.id ? item.id : null),
      });
      this.mapboxSuggestionsByQuery[queryKey] = filteredMapboxFeatures;
      if (resultsPayload && Array.isArray(resultsPayload.features)) {
        resultsPayload.features = filteredMapboxFeatures;
      }
      if (this.geocoder && this.geocoder._typeahead && typeof this.geocoder._typeahead.update === 'function') {
        this.geocoder._typeahead.update(filteredMapboxFeatures);
      }

      this.scheduleMergedSuggestionsRender(queryKey);
    },

    scheduleMergedSuggestionsRender(queryKey) {
      if (this.geocoderRenderDebounceTimer) {
        clearTimeout(this.geocoderRenderDebounceTimer);
      }

      this.geocoderRenderDebounceTimer = setTimeout(() => {
        this.renderMergedSuggestionsForQuery(queryKey);
      }, 0);
    },

    renderMergedSuggestionsForQuery(queryKey) {
      if (!queryKey || queryKey.length < 2 || !this.geocoder || !this.geocoder._typeahead) {
        return;
      }

      const currentInputKey = this.normalizeAutosuggestQueryKey(this.resolveGeocoderInputValue());
      if (currentInputKey !== queryKey) {
        return;
      }

      const externalSuggestions = this.isOfferSuggestionsEnabled()
        ? (Array.isArray(this.externalSuggestionsByQuery[queryKey]) ? this.externalSuggestionsByQuery[queryKey] : [])
        : [];
      const tourismRegionSuggestions = this.isTourismRegionSuggestionsEnabled()
        ? (Array.isArray(this.tourismRegionSuggestionsByQuery[queryKey]) ? this.tourismRegionSuggestionsByQuery[queryKey] : [])
        : [];
      const mapboxSuggestions = Array.isArray(this.mapboxSuggestionsByQuery[queryKey])
        ? this.mapboxSuggestionsByQuery[queryKey]
        : [];

      const mapboxSuggestionsWithHeading = mapboxSuggestions.map((feature, index) => {
        const properties = feature && feature.properties ? { ...feature.properties } : {};
        if (index === 0) {
          properties.mapboxSuggestionLabel = this.resolveMapboxSuggestionLabel();
        }

        return {
          ...feature,
          properties,
        };
      });

      const mergedFeatures = tourismRegionSuggestions
        .concat(externalSuggestions)
        .concat(mapboxSuggestionsWithHeading)
        .slice(0, this.resolveGeocoderSuggestionRenderLimit());

      this.geocoder._typeahead.update(mergedFeatures);
      this.appendMapboxAttributionFooter();
    },

    appendMapboxAttributionFooter() {
      const suggestionsList = this.$el.querySelector('.mapboxgl-ctrl-geocoder .suggestions');
      if (!suggestionsList) {
        return;
      }

      const existingFooter = suggestionsList.querySelector('.mapboxgl-ctrl-geocoder--powered-by');
      if (existingFooter) {
        return;
      }

      const footerItem = document.createElement('li');
      footerItem.className = 'mapboxgl-ctrl-geocoder--powered-by';
      footerItem.innerHTML = '<a href="https://www.mapbox.com/" target="_blank" rel="noopener noreferrer">Powered by Mapbox</a>';
      suggestionsList.appendChild(footerItem);
    },
  },
};
